import * as React from "react";

import {
  Avatar,
  Button,
  CardActions,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DeleteIcon from "@mui/icons-material/Delete";
import OrganizationIcon from "@mui/icons-material/Edit";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useQueryClient } from "@tanstack/react-query";
import AddOrganizationAdmin from "../components/AddOrganizationAdmin";
import AddOrganizationOwner from "../components/AddOrganizationOwner";
import { UserIcon } from "../components/brandlibrary/MingleIcons";
import ButtonLeague from "../components/ButtonLeague";
import { useLeague } from "../hooks/useLeague";
import { useOrganization } from "../hooks/useOrganization";
import {
  addAdminToOrganization,
  removeAdminFromOrganization,
  setOrganizationOwner,
} from "../hooks/useOrganizations";
import useTeams from "../hooks/useTeams";
import { ISearchTeamsParams, ITeam, IUsers } from "../utils/api.interfaces";

export default function Organization() {
  const { organizationId } = useParams();
  const { dataOrganization, refetch: refetchOrganization } =
    useOrganization(organizationId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State for admin management
  const [showAddAdminDialog, setShowAddAdminDialog] = React.useState(false);
  const [showAddOwnerDialog, setShowAddOwnerDialog] = React.useState(false);
  const [deletingAdminId, setDeletingAdminId] = React.useState<string | null>(
    null
  );
  const [isAddingAdmin, setIsAddingAdmin] = React.useState(false);
  const [isSettingOwner, setIsSettingOwner] = React.useState(false);
  const [isDeletingAdmin, setIsDeletingAdmin] = React.useState(false);
  const initialSearchParams: ISearchTeamsParams = {
    page: "0",
    TeamNameFilter: "",
    OrganizationIdFilter: organizationId || "",
    LeagueIdFilter: "",
    pageSize: "1000",
  };
  const { isLoading, dataTeams } = useTeams(initialSearchParams);
  const { dataLeague } = useLeague(dataOrganization?.leagueId || "");

  // Sort: active before archived, then org-managed before unmanaged, then most members first.
  const sortedTeams = React.useMemo(
    () =>
      [...(dataTeams?.teams ?? [])].sort(
        (a, b) =>
          Number(a.isArchived ?? false) - Number(b.isArchived ?? false) ||
          Number(b.isManagedByOrganization ?? false) -
            Number(a.isManagedByOrganization ?? false) ||
          (b.members?.length ?? 0) - (a.members?.length ?? 0)
      ),
    [dataTeams]
  );
  console.log("dataOrganization", dataOrganization);
  const handleAddAdmin = async (user: IUsers) => {
    if (!organizationId) return;

    setIsAddingAdmin(true);
    try {
      await addAdminToOrganization(organizationId, user.id);
      await refetchOrganization();
      setShowAddAdminDialog(false);
    } catch (error) {
      console.error("Error adding admin:", error);
    } finally {
      setIsAddingAdmin(false);
    }
  };

  const handleSetOwner = async (user: IUsers) => {
    if (!organizationId) return;

    setIsSettingOwner(true);
    try {
      await setOrganizationOwner(organizationId, user.id);
      await refetchOrganization();
      setShowAddOwnerDialog(false);
    } catch (error) {
      console.error("Error setting owner:", error);
    } finally {
      setIsSettingOwner(false);
    }
  };

  const handleDeleteAdmin = async (adminId: string) => {
    if (!organizationId) return;

    setIsDeletingAdmin(true);
    try {
      await removeAdminFromOrganization(organizationId, adminId);
      await refetchOrganization();
      setDeletingAdminId(null);
    } catch (error) {
      console.error("Error removing admin:", error);
    } finally {
      setIsDeletingAdmin(false);
    }
  };

  return (
    <>
      <Typography
        component="h1"
        variant="h1"
        color="inherit"
        noWrap
        gutterBottom
        sx={{
          textAlign: "center",
          margin: "40px 0px",
        }}
      >
        Organization detail page
      </Typography>
      <Stack
        spacing={3}
        sx={{
          flexDirection: "column",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          alignContent: "center",
        }}
      >
        <Card sx={{ width: "100%", maxWidth: "850px", margin: "auto" }}>
          <CardContent>
            <Typography
              variant="h1"
              color="text.secondary"
              align="center"
              gutterBottom
            >
              {dataOrganization?.name}
            </Typography>

            <Avatar
              src={dataOrganization?.logoUrl}
              sx={{ width: 175, height: 175, margin: "auto" }}
            />

            <TableContainer>
              <Table aria-label="user details table">
                <TableBody>
                  <TableRow>
                    <TableCell
                      component="th"
                      scope="row"
                      colSpan={2}
                    ></TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell component="th" scope="row">
                      id:
                    </TableCell>
                    <TableCell align="right">{dataOrganization?.id}</TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell component="th" scope="row">
                      shortName:
                    </TableCell>
                    <TableCell align="right">
                      {dataOrganization?.shortName}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      League:
                    </TableCell>
                    <TableCell align="right">
                      <ButtonLeague leagueId={dataLeague?.id} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      isGhost:
                    </TableCell>
                    <TableCell align="right">
                      {dataOrganization?.isGhost.toString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Type:
                    </TableCell>
                    <TableCell align="right">
                      {dataOrganization?.type}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      CountryCode:
                    </TableCell>
                    <TableCell align="right">
                      {dataOrganization?.countryCode}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      SportType:
                    </TableCell>
                    <TableCell align="right">
                      {dataOrganization?.sportType}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Owner:
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        {dataOrganization?.owner ? (
                          <>
                            <Avatar
                              src={dataOrganization.owner.imageUrl}
                              sx={{ width: 30, height: 30 }}
                            />
                            <Typography variant="body2">
                              {dataOrganization.owner.displayName ||
                                `${dataOrganization.owner.firstName} ${dataOrganization.owner.lastName}`}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Owner not set yet
                          </Typography>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setShowAddOwnerDialog(true)}
                          sx={{ ml: 2 }}
                        >
                          {dataOrganization?.owner ? "Change" : "Set"} Owner
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      Admins:
                    </TableCell>
                    <TableCell align="right">
                      <Stack spacing={1}>
                        {dataOrganization?.admins &&
                        dataOrganization.admins.length > 0 ? (
                          <Stack
                            direction="column"
                            spacing={1}
                            alignItems="flex-end"
                          >
                            {dataOrganization.admins.map((admin) => (
                              <Stack
                                key={admin.id}
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Avatar
                                  src={admin.imageUrl}
                                  sx={{ width: 30, height: 30 }}
                                />
                                <Typography variant="body2">
                                  {admin.displayName ||
                                    `${admin.firstName} ${admin.lastName}`}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => setDeletingAdminId(admin.id)}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Admins not set yet
                          </Typography>
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setShowAddAdminDialog(true)}
                          startIcon={<AddIcon />}
                          sx={{ alignSelf: "flex-end" }}
                        >
                          Add Admin
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {/*<TableRow>*/}
                  {/*  <TableCell colSpan={2}>*/}
                  {/*    <Stack direction="column" spacing={2}>*/}
                  {/*      <Typography*/}
                  {/*        variant="h4"*/}
                  {/*        color="text.secondary"*/}
                  {/*        gutterBottom*/}
                  {/*      >*/}
                  {/*        Export organization data*/}
                  {/*      </Typography>*/}
                  {/*      <ButtonExportData*/}
                  {/*        organizationId={dataOrganization?.id}*/}
                  {/*      />*/}
                  {/*    </Stack>*/}
                  {/*  </TableCell>*/}
                  {/*</TableRow>*/}
                  <TableRow>
                    <TableCell component="th" scope="row">
                      address:
                    </TableCell>
                    <TableCell align="right">
                      {dataOrganization?.address?.line1}
                      <br />
                      {dataOrganization?.address?.line2}
                      <br />
                      {dataOrganization?.address?.zipCode}
                      <br />
                      {dataOrganization?.address?.city}
                      <br />
                      {dataOrganization?.address?.region}
                      <br />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      location:
                    </TableCell>
                    <TableCell align="right">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${dataOrganization?.address?.coordinates?.lat},${dataOrganization?.address?.coordinates?.lon}`}
                        target="_blank"
                      >
                        <img src={dataOrganization?.address?.mapImageUrl} />
                        <br />
                        {dataOrganization?.address?.coordinates?.lat},
                        {dataOrganization?.address?.coordinates?.lon}
                      </a>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography
                        variant="h2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Organization teams
                      </Typography>
                      <Table size="small" aria-label="organization teams">
                        <TableHead>
                          <TableRow>
                            <TableCell>Team</TableCell>
                            <TableCell>Season</TableCell>
                            <TableCell align="center">Members</TableCell>
                            <TableCell align="center">Managed by org</TableCell>
                            <TableCell align="center">Archived</TableCell>
                            <TableCell align="right"></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedTeams.map((team: ITeam) => (
                            <TableRow key={team.id} hover>
                              <TableCell>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Avatar
                                    src={team.logoUrl}
                                    sx={{ width: 30, height: 30 }}
                                  />
                                  <Stack>
                                    <Typography variant="body2">
                                      {team.name || "no team name"}
                                    </Typography>
                                    {team.nameAbbreviation && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {team.nameAbbreviation}
                                      </Typography>
                                    )}
                                  </Stack>
                                </Stack>
                              </TableCell>
                              <TableCell>{team.season?.name ?? "-"}</TableCell>
                              <TableCell align="center">
                                {team.members?.length ?? 0}
                              </TableCell>
                              <TableCell align="center">
                                {team.isManagedByOrganization && (
                                  <Chip
                                    label="Managed"
                                    size="small"
                                    color="primary"
                                  />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={
                                    team.isArchived ? "Archived" : "Active"
                                  }
                                  size="small"
                                  color={
                                    team.isArchived ? "default" : "success"
                                  }
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  component={Link}
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  to={`/teams/${team.id}`}
                                >
                                  detail
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>

          <CardActions>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="space-between"
              spacing={2}
              sx={{ width: "100%", padding: 2 }}
            >
              <Button
                component={Link}
                size="small"
                variant="contained"
                color="success"
                to={`/organizations/add/`}
                startIcon={<UserIcon />}
              >
                add new Organization
              </Button>
              <Button
                component={Link}
                size="small"
                variant="outlined"
                color="secondary"
                to={`/organizations/edit/${dataOrganization?.id}`}
                startIcon={<OrganizationIcon />}
              >
                edit
              </Button>
            </Stack>
          </CardActions>
        </Card>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="space-between"
          spacing={2}
          sx={{ width: "100%", maxWidth: "850px", margin: "auto" }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate(-1)}
            startIcon={<ChevronLeftIcon />}
          >
            back
          </Button>
        </Stack>
      </Stack>

      {/* Add Admin Dialog */}
      <Dialog
        open={showAddAdminDialog}
        onClose={() => setShowAddAdminDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Organization Admin</DialogTitle>
        <DialogContent>
          <AddOrganizationAdmin onAdminSelected={handleAddAdmin} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddAdminDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Add Owner Dialog */}
      <Dialog
        open={showAddOwnerDialog}
        onClose={() => setShowAddOwnerDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Set Organization Owner</DialogTitle>
        <DialogContent>
          <AddOrganizationOwner onOwnerSelected={handleSetOwner} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddOwnerDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Admin Confirmation Dialog */}
      <Dialog open={!!deletingAdminId} onClose={() => setDeletingAdminId(null)}>
        <DialogTitle>Remove Admin</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this admin from the organization?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingAdminId(null)}>Cancel</Button>
          <Button
            onClick={() =>
              deletingAdminId && handleDeleteAdmin(deletingAdminId)
            }
            color="error"
            disabled={isDeletingAdmin}
          >
            {isDeletingAdmin ? <CircularProgress size={20} /> : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
